import { useEffect, useMemo, useState } from "react";

import { pickName, useT } from "../lib/i18n.jsx";
import { buildSteps } from "./lib/directions.js";

/** Active-route summary: destination, distance/time, what the route is
 *  (item 0111), and the turn-by-turn step list (item 0110).
 *
 *  Styled as the POI panel's sibling, not as a different product (item 0091):
 *  same dark surface, same radius, same type scale. Once someone has asked for
 *  directions this IS the interface -- so the time and distance get real
 *  weight, and on a phone it takes the bottom of the screen while the panel
 *  collapses to its logo (item 0093). Cancel stays reachable, but it is no
 *  longer the only way to change your mind: picking another POI re-routes.
 *
 *  The step list EXPANDS the banner rather than replacing it: collapsed, this
 *  is exactly the banner that shipped before. It opens by default on a totem or
 *  a desktop, where there is room beside the map, and stays closed on a phone,
 *  where the visitor scanned a QR to look at the map. Either way it is capped
 *  and scrolls -- on a totem the map is the point and a long corridor must not
 *  push it off the screen.
 */

const P = {
  stroke: "currentColor",
  strokeWidth: 1.9,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  fill: "none",
};

const ARROW_STRAIGHT = <path {...P} d="M12 20V6M8 10l4-4 4 4" />;
// One right-angle hook, drawn once and mirrored/rotated. A sharp turn is the
// same hook tipped over, which is what a sharp turn looks like.
const HOOK = (
  <g {...P}>
    <path d="M16 20v-7a4 4 0 0 0-4-4H8" />
    <path d="M11 6L8 9l3 3" />
  </g>
);
const DIAGONAL = (
  <g {...P}>
    <path d="M14 20v-6L9 9" />
    <path d="M9 13V9h4" />
  </g>
);
const mirror = (glyph) => <g transform="translate(24 0) scale(-1 1)">{glyph}</g>;

const STEP_ICONS = {
  continue: ARROW_STRAIGHT,
  left: HOOK,
  right: mirror(HOOK),
  slight_left: DIAGONAL,
  slight_right: mirror(DIAGONAL),
  sharp_left: <g transform="rotate(28 12 12)">{HOOK}</g>,
  sharp_right: mirror(<g transform="rotate(28 12 12)">{HOOK}</g>),
  uturn: (
    <g {...P}>
      <path d="M8 20v-8a4 4 0 0 1 8 0v8" />
      <path d="M5 17l3 3 3-3" />
    </g>
  ),
  stairs: (
    <g {...P}>
      <path d="M4 19h4v-4h4v-4h4V7h4" />
    </g>
  ),
  elevator: (
    <g {...P}>
      <rect x="5" y="3.5" width="14" height="17" rx="2" />
      <path d="M9.5 11L12 8.5l2.5 2.5M9.5 14l2.5 2.5 2.5-2.5" />
    </g>
  ),
  escalator: (
    <g {...P}>
      <path d="M4 18h4l8-9h4" />
      <path d="M16 6h4v4" />
    </g>
  ),
  arrive: (
    <g {...P}>
      <path d="M12 21s6-5.4 6-10a6 6 0 1 0-12 0c0 4.6 6 10 6 10z" />
      <circle cx="12" cy="11" r="2.2" />
    </g>
  ),
};

const TRANSITION_ICON = { elevator: "elevator", escalator: "escalator", stairs: "stairs" };

function StepIcon({ name }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" className="shrink-0">
      {STEP_ICONS[name] ?? STEP_ICONS.continue}
    </svg>
  );
}

export default function RouteBanner({
  route,
  venue,
  activeFloorId,
  onFloorSwitch,
  onCancel,
  onFocusStep,
  compact = false,
  atTop = false,
  stacked = false,
  menuPeek = true,
  onStepsOpened,
}) {
  const { t, lang } = useT();
  const floorsById = Object.fromEntries(venue.floors.map((f) => [f.id, f]));

  const segment =
    route.segments.find((s) => s.floor_id === activeFloorId) ?? route.segments[0];
  const transition = segment?.transition_out;
  const lastFloorId = route.segments[route.segments.length - 1]?.floor_id;

  const instructionKey = transition ? `route_take_${transition.kind}` : null;

  const destinationName = pickName(route.poi?.name, lang);
  // Derived from geometry the client already has, so switching language
  // re-renders the sentences without refetching the route (directions.js).
  const steps = useMemo(
    () => buildSteps(route, { destinationName }),
    [route, destinationName],
  );

  const [open, setOpen] = useState(!compact);
  useEffect(() => setOpen(!compact), [compact, route]);
  // Stacked band, menu expanded by the visitor: the card needs the room, so the
  // step list folds. Opening it again hands the room back (onStepsOpened).
  const menuTakesRoom = stacked && !menuPeek;
  useEffect(() => {
    if (menuTakesRoom) setOpen(false);
  }, [menuTakesRoom]);

  // There is no live position at a totem, so "current" is the first step on the
  // floor being looked at. It moves when the visitor changes floor, which is
  // the only signal of progress the viewer actually has -- claiming more than
  // that would be inventing it.
  const currentId = steps.find((s) => s.floorId === activeFloorId)?.id;

  const floorLabel = (id) =>
    `${t("floor_label")} ${floorsById[id]?.short_label ?? ""}`.trim();

  const badges = route.badges ?? [];

  return (
    <div
      className={
        "z-20 flex flex-col rounded-2xl bg-[#26262c]/95 " +
        (stacked ? "pointer-events-auto relative min-h-0 " : "absolute ") +
        // Stacked band: the panel itself scrolls as a last resort, so nothing
        // in it -- the floor-change button least of all -- can ever be clipped
        // (QA on 751d359). Elsewhere only the step list scrolls, as before.
        (stacked ? "overflow-x-hidden overflow-y-auto " : "overflow-hidden ") +
        "p-4 text-white shadow-2xl backdrop-blur " +
        // Never taller than the viewport, at any size: the step list scrolls
        // INSIDE the panel (see the <ol> below) instead of the panel running
        // off the bottom of a short screen (item 0151).
        // (The stacked band below caps it tighter still.)
        // Docked right above phone width, it stops 96px short of the bottom
        // so the "open on your phone" button below it stays visible and
        // tappable (item 0324); the step list scrolls inside, as it always has.
        (stacked ? "" : compact ? "max-h-[calc(100vh-1rem)] " : "max-h-[calc(100vh-6rem)] ") +
        // Stacked -- the 641-819px band, and every top-docked layout (item
        // 0373) -- is in flow in ViewerPage's column, under the menu: no
        // position of its own, full width of the column.
        (stacked
          ? ""
          : compact
          ? // Bottom while the menu is collapsed -- thumb-reachable, and the
            // primary surface once someone has asked for directions. If the
            // visitor reopens the menu (to route somewhere else) the sheet
            // needs that space, so the banner moves to the top rather than
            // stacking on top of it.
            atTop
            ? "inset-x-2 top-2"
            : "inset-x-2 bottom-2"
          : // Docked RIGHT, not centred (item 0151). Centred, this panel is
            // 460px wide about the middle, so its left edge crosses the POI
            // card's right edge (352px) on every viewport under 1164px --
            // including a 1080x1920 portrait totem, which is a shipping
            // configuration, not an edge case. It cut the card's "Compartir"
            // button in half.
            //
            // left/right are both set with `ml-auto`, so the panel hugs the
            // right gutter when there is room and SHRINKS rather than sliding
            // left when there is not. 364px = the card's right edge + 12px;
            // 68px = the floor pills' column (right-3 + w-11) + 12px. Below
            // 820px there is no useful room left -- see useSideBySide.
            //
            // Between 641px and 819px there is no room beside the card, and
            // above phone width the card no longer yields (item 0314), so the
            // two STACK in one column that ViewerPage lays out: this panel sits
            // in flow under the card (or under its 56px bar while the visitor
            // is following the route) and takes whatever height is left.
            "left-[364px] right-[68px] top-3 ml-auto max-w-[460px]")
      }
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {t("route_heading")}
          </div>
          <div className="truncate text-lg font-bold leading-tight">{destinationName}</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold leading-none text-[#ff6b78]">
              {t("route_minutes", { min: Math.max(1, Math.round(route.minutes)) })}
            </span>
            <span className="text-sm font-semibold text-slate-300">
              {t("route_meters", { m: Math.round(route.total_m) })}
            </span>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="shrink-0 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold
                     text-white active:bg-white/20"
        >
          {t("cancel")}
        </button>
      </div>

      {/* What this route IS, computed from the edges it actually uses. Absent
          when nothing true can be said about it. */}
      {badges.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {badges.map((b) => (
            <span
              key={b}
              className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-slate-200"
            >
              {t(`route_badge_${b}`)}
            </span>
          ))}
        </div>
      )}

      {transition && (
        <button
          onClick={() => onFloorSwitch(transition.to_floor_id)}
          className="mt-3 w-full rounded-xl bg-[#e63946] px-3 py-3 text-sm font-bold text-white
                     active:bg-[#c92e3b]"
        >
          {t(instructionKey, {
            floor: floorsById[transition.to_floor_id]?.short_label ?? "",
          })}{" "}
          →
        </button>
      )}
      {!transition && activeFloorId === lastFloorId && route.segments.length > 1 && (
        <div className="mt-3 rounded-xl bg-emerald-500/15 px-3 py-2 text-center text-xs font-semibold text-emerald-300">
          {t("route_arrived")}
        </div>
      )}

      {steps.length > 0 && (
        <>
          <button
            onClick={() => {
              if (!open) onStepsOpened?.();
              setOpen((v) => !v);
            }}
            className="mt-3 flex w-full items-center justify-between rounded-xl bg-white/5 px-3 py-2
                       text-xs font-semibold text-slate-200 active:bg-white/15"
          >
            <span>
              {t(open ? "steps_hide" : "steps_show")} · {steps.length}
            </span>
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path {...P} d={open ? "M6 14l6-6 6 6" : "M6 10l6 6 6-6"} />
            </svg>
          </button>

          {open && (
            // min-h-0 is load bearing: without it a flex child refuses to
            // shrink below its content and `overflow-y-auto` never engages,
            // so the list pushes the panel past the bottom of the screen
            // rather than scrolling within it.
            // Stacked band: never fewer than ~2 steps (min-h 5.5rem); if that
            // does not fit either, the panel itself scrolls (see above).
            <ol
              className={
                "mt-2 max-h-[38vh] flex-1 space-y-1 overflow-y-auto pr-1 " +
                (stacked ? "min-h-[5.5rem]" : "min-h-0")
              }
            >
              <li className="flex items-center gap-2 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <span className="h-2 w-2 rounded-full bg-slate-400" />
                {t("you_are_here")}
                <span className="ml-auto normal-case text-slate-500">
                  {floorLabel(route.segments[0]?.floor_id)}
                </span>
              </li>
              {steps.map((step) => {
                const isCurrent = step.id === currentId;
                const icon =
                  step.kind === "transition"
                    ? (TRANSITION_ICON[step.transition] ?? "elevator")
                    : step.kind === "arrive"
                      ? "arrive"
                      : (step.turn ?? "continue");
                const showFloor =
                  step.kind === "transition" || step.kind === "arrive" || null;
                return (
                  <li key={step.id}>
                    <button
                      type="button"
                      onClick={() => onFocusStep?.(step)}
                      className={
                        "flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left text-sm " +
                        (isCurrent
                          ? "bg-white/15 font-semibold text-white"
                          : "text-slate-300 active:bg-white/10")
                      }
                    >
                      <span className={isCurrent ? "text-[#ff6b78]" : "text-slate-400"}>
                        <StepIcon name={icon} />
                      </span>
                      <span className="min-w-0 flex-1 leading-snug">
                        {t(step.key, {
                          ...step.vars,
                          floor:
                            floorsById[step.toFloorId]?.short_label ??
                            floorsById[step.floorId]?.short_label ??
                            "",
                        })}
                        {showFloor && (
                          <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-slate-300">
                            {floorLabel(
                              step.kind === "transition" ? step.toFloorId : step.floorId,
                            )}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </>
      )}
    </div>
  );
}
